import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { AlertTriangle, Upload, FileText, CheckCircle, X } from 'lucide-react';
import { API_BASE_URL } from '../lib/api';
import { getTransactionGrossAmount, getTransactionGrossCurrency } from '../lib/merchantAmounts';

interface Payment {
    transaction_id: string;
    payment_id: string;
    user_id?: string;
    merchantPaymentId?: string;
    merchantWithdrawalId?: string;
    order_type?: string;
    amount?: number;
    currency?: string;
    payer_amount?: number;
    payer_currency?: string;
    receiver_amount?: number;
    receiver_currency?: string;
    created_at?: string;
}

interface AppealModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    payment: Payment | null;
    apiKey: string;
}

export const AppealModal: React.FC<AppealModalProps> = ({
    open,
    onOpenChange,
    payment,
    apiKey
}) => {
    const [description, setDescription] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async () => {
        if (!payment?.transaction_id) return;
        setLoading(true);
        setError(null);
        setResult(null);

        if (payment?.created_at) {
            const transactionDate = new Date(payment.created_at);
            const daysSinceCreation = (Date.now() - transactionDate.getTime()) / (1000 * 60 * 60 * 24);
            if (daysSinceCreation > 14) {
                setError('Невозможно создать апелляцию: транзакция старше 14 дней');
                setLoading(false);
                return;
            }
        }

        if (files.length === 0) {
            setError('Для создания апелляции необходимо прикрепить файл подтверждения');
            setLoading(false);
            return;
        }

        try {
            const formData = new FormData();
            formData.append('transaction_id', payment.transaction_id);
            if (description) {
                formData.append('description', description);
            }
            files.forEach((file) => formData.append('files', file));

            const response = await fetch(`${API_BASE_URL}/api/merchant/appeals`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiKey}`,
                },
                body: formData
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || 'Ошибка при создании апелляции');
            }

            setResult(data);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setDescription('');
        setFiles([]);
        setResult(null);
        setError(null);
        onOpenChange(false);
    };

    const formatAmount = (amount?: number, currency?: string) => {
        if (amount === undefined || amount === null) return '';

        if (currency === 'USDT' || currency === 'BTC' || currency === 'ETH') {
            return `${amount.toFixed(2)} ${currency}`;
        }

        try {
            return new Intl.NumberFormat('ru-RU', {
                style: 'currency',
                currency: currency || 'RUB',
                minimumFractionDigits: 2
            }).format(amount);
        } catch (error) {
            return `${amount.toFixed(2)} ${currency || 'RUB'}`;
        }
    };

    const grossAmount = payment ? getTransactionGrossAmount(payment) : undefined;
    const grossCurrency = payment ? getTransactionGrossCurrency(payment) : 'RUB';
    const handleFilesSelected = (selected: FileList | null) => {
        if (!selected) return;
        const next = [...files, ...Array.from(selected)];
        if (next.length > 10) {
            setError('Можно прикрепить не более 10 файлов');
            return;
        }
        const invalid = next.find((item) => item.size > 10 * 1024 * 1024);
        if (invalid) {
            setError(`Файл «${invalid.name}» превышает лимит 10 МБ`);
            return;
        }
        setError(null);
        setFiles(next);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-orange-600 dark:text-orange-300">
                        <AlertTriangle className="h-5 w-5" />
                        Открыть спор по транзакции
                    </DialogTitle>
                    <DialogDescription>
                        Создайте апелляцию, если возникла проблема с платежом
                    </DialogDescription>
                </DialogHeader>

                {!result ? (
                    <div className="space-y-4 mt-4">
                        {/* Информация о транзакции */}
                        <div className="p-4 bg-gray-50 dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700">
                            <p className="text-sm font-medium text-gray-600 dark:text-slate-300 mb-2">Информация о транзакции</p>
                            <div className="space-y-1">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-500 dark:text-slate-400">Merchant ID:</span>
                                    <span className="text-sm font-medium">
                                        {payment?.merchantPaymentId || payment?.merchantWithdrawalId || '-'}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-500 dark:text-slate-400">Client ID:</span>
                                    <span className="text-sm font-medium">{payment?.user_id || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-500 dark:text-slate-400">System Payment ID:</span>
                                    <span className="text-xs font-mono text-gray-700 dark:text-slate-200">{payment?.payment_id}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-500 dark:text-slate-400">Transaction ID:</span>
                                    <span className="text-xs font-mono text-gray-700 dark:text-slate-200">{payment?.transaction_id}</span>
                                </div>
                                {grossAmount !== undefined && (
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-500 dark:text-slate-400">Сумма до комиссии:</span>
                                        <span className="text-sm font-medium">
                                            {formatAmount(grossAmount, grossCurrency)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Описание проблемы</Label>
                            <Textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Опишите причину спора..."
                                rows={4}
                                className="resize-none"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Файл подтверждения (Чек/Скриншот) <span className="text-red-500">*</span></Label>
                            <div className="border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-lg p-4 flex flex-col items-center justify-center hover:bg-gray-50 dark:hover:bg-slate-900 transition-colors">
                                <Input
                                    type="file"
                                    multiple
                                    onChange={(e) => {
                                        handleFilesSelected(e.target.files);
                                        e.target.value = '';
                                    }}
                                    className="hidden"
                                    id="appeal-file-upload"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                />
                                <Label
                                    htmlFor="appeal-file-upload"
                                    className="cursor-pointer flex flex-col items-center gap-2"
                                >
                                    {files.length > 0 ? (
                                        <>
                                            <FileText className="h-8 w-8 text-blue-500" />
                                            <span className="text-sm font-medium text-gray-900 dark:text-slate-100">
                                                Выбрано файлов: {files.length}
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-slate-400">Нажмите, чтобы добавить ещё</span>
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="h-8 w-8 text-gray-400" />
                                            <span className="text-sm font-medium text-gray-600 dark:text-slate-300">Нажмите для выбора файлов</span>
                                            <span className="text-xs text-gray-400">До 10 файлов, PDF/JPG/PNG, каждый до 10 МБ</span>
                                        </>
                                    )}
                                </Label>
                            </div>
                            {files.length > 0 && (
                                <div className="space-y-2">
                                    {files.map((item, index) => (
                                        <div key={`${item.name}-${item.lastModified}-${index}`} className="flex items-center justify-between rounded border p-2 text-sm">
                                            <span className="truncate">{item.name}</span>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setFiles(files.filter((_, fileIndex) => fileIndex !== index))}
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {error && (
                            <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                                <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-300 mt-0.5 flex-shrink-0" />
                                <span className="text-sm text-red-800 dark:text-red-200">{error}</span>
                            </div>
                        )}

                        <div className="flex gap-3 pt-2">
                            <Button
                                variant="outline"
                                onClick={handleClose}
                                className="flex-1"
                            >
                                Отмена
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={loading || files.length === 0}
                                className="flex-1 bg-orange-600 hover:bg-orange-700"
                            >
                                {loading ? 'Отправка...' : 'Создать апелляцию'}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="mt-4">
                        <div className="p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
                            <div className="flex items-center gap-3 mb-3">
                                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-300" />
                                <h3 className="font-semibold text-green-900 dark:text-green-200">Апелляция успешно создана!</h3>
                            </div>
                            <div className="space-y-2 text-sm text-green-800 dark:text-green-200">
                                <p>ID Апелляции: <span className="font-mono font-bold">{result.appeal_id}</span></p>
                                <p>Статус: <span className="uppercase font-medium">{result.status}</span></p>
                                <p className="text-xs text-green-700 dark:text-green-300 opacity-80 mt-2">
                                    Webhook уведомление отправлено.
                                </p>
                            </div>
                        </div>
                        <Button
                            onClick={handleClose}
                            className="w-full mt-4"
                        >
                            Закрыть
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
